/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { cssFns } from "@superweb/css";
import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  version,
  type ReactNode,
} from "react";

import { DialogContextProvider } from "./dialog/dialog";
import { AppMenuContextProvider } from "./experimental-app-menu/context";
import { GuidanceOverlayContextProvider } from "./experimental-guidance-overlay";
import {
  FloatingDialogContextProvider,
  FloatingPanelContextProvider,
} from "./floating";
import { useIsMobile } from "./mobile-context";
import { PopoverContextProvider } from "./popover";
import { SnackbarContainer } from "./snackbar/snackbar-container";
import { useDebouncedState } from "./state/state";
import { useUiColors } from "./theme";
import { SlideoverContextProvider } from "./slideover/slideover";
import { OverlayContextProvider } from "./overlay";

const inertValue = version.startsWith("19") ? true : "true";

const useBrowserLayoutEffect =
  // reason for check window.document - https://github.com/adobe/react-spectrum/issues/7926#issuecomment-2721587586
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  typeof window !== "undefined" && window.document ? useLayoutEffect : () => {};

export const AppContainer = ({ children }: { children: ReactNode }) => {
  const uiColors = useUiColors();
  const isMobile = useIsMobile();

  const appMenuContainerRef = useRef<HTMLDivElement>(null);
  const childrenContainerRef = useRef<HTMLDivElement>(null);
  const dialogContainerRef = useRef<HTMLDivElement>(null);
  const floatingContainerRef = useRef<HTMLDivElement>(null);
  const floatingPanelContainerRef = useRef<HTMLDivElement>(null);
  const floatingDialogContainerRef = useRef<HTMLDivElement>(null);
  const slideoverContainerRef = useRef<HTMLDivElement>(null);
  const closeCallbacksRef = useRef<
    Map<string, { callback: () => void; container: HTMLElement | null }>
  >(new Map());
  const [floatingOffsetInline, setFloatingOffsetInline] = useState(0);
  const [floatingOffsetBlock, setFloatingOffsetBlock] = useState(0);
  const [dialogStack, setDialogStack] = useState<unknown[]>([]);
  const [floatingDialogInstance, setFloatingDialogInstance] =
    useState<unknown>();
  const [popoverContainer, setPopoverContainer] =
    useState<HTMLDivElement | null>(null);
  const [guidanceOverlayContainer, setGuidanceOverlayContainer] =
    useState<HTMLDivElement | null>(null);

  const isFloatingDialogOpen = Boolean(floatingDialogInstance);
  const isRegularDialogOpen = dialogStack.length > 0;
  const isAnyDialogOpen = isRegularDialogOpen || isFloatingDialogOpen;
  const debouncedDialogOpen = useDebouncedState(isAnyDialogOpen, 0);

  useBrowserLayoutEffect(() => {
    const floatingContainer = floatingContainerRef.current;
    const dialogContainer =
      dialogContainerRef.current || floatingDialogContainerRef.current;
    if (isAnyDialogOpen && floatingContainer && dialogContainer) {
      const { clientWidth, clientHeight } = document.documentElement;
      if (floatingOffsetInline === 0) {
        const vScrollWidth = window.innerWidth - clientWidth;
        floatingContainer.style.paddingInlineEnd = `${vScrollWidth}px`;
        floatingContainer.style.paddingBlockStart = `${vScrollWidth}px`;
      }
      if (floatingOffsetBlock === 0) {
        const hScrollWidth = window.innerHeight - clientHeight;
        floatingContainer.style.paddingBlockEnd = `${hScrollWidth}px`;
      }
      return () => {
        floatingContainer.removeAttribute("style");
      };
    } else {
      return;
    }
  }, [isAnyDialogOpen, floatingOffsetBlock, floatingOffsetInline]);

  useBrowserLayoutEffect(() => {
    const childrenContainer = childrenContainerRef.current;
    const dialogContainer =
      dialogContainerRef.current || floatingDialogContainerRef.current;
    if (isAnyDialogOpen && childrenContainer && dialogContainer) {
      const { scrollTop, scrollLeft, clientWidth } = document.documentElement;
      const scrollWidth = window.innerWidth - clientWidth;
      childrenContainer.style.width = `calc(100vw - ${scrollWidth}px`;
      childrenContainer.style.height = "100vh";
      childrenContainer.style.overflow = "hidden";
      childrenContainer.style.position = "fixed";
      childrenContainer.style.top = "0px";
      childrenContainer.style.left = "0px";
      childrenContainer.style.paddingRight = `${scrollWidth}px`;
      childrenContainer.scrollTop = scrollTop;
      childrenContainer.scrollLeft = scrollLeft;
      return () => {
        childrenContainer.removeAttribute("style");
        document.documentElement.scrollTop = scrollTop;
        document.documentElement.scrollLeft = scrollLeft;
      };
    } else {
      return;
    }
  }, [isAnyDialogOpen]);

  const handleSetFloatingOffset = useCallback(
    ({ block, inline }: { block?: number; inline?: number }) => {
      setFloatingOffsetInline((value) => inline ?? value);
      setFloatingOffsetBlock((value) => block ?? value);
    },
    [],
  );

  return (
    <AppMenuContextProvider value={{ containerRef: appMenuContainerRef }}>
      <DialogContextProvider
        value={{
          containerRef: dialogContainerRef,
          stack: dialogStack,
          onStackChange: setDialogStack,
        }}
      >
        <SlideoverContextProvider
          value={{
            container: slideoverContainerRef,
            closeCallbacksRef,
          }}
        >
          <PopoverContextProvider
            value={{
              container: popoverContainer,
            }}
          >
            <FloatingDialogContextProvider
              value={{
                containerRef: floatingDialogContainerRef,
                dialogInstance: floatingDialogInstance,
                onDialogInstanceChange: setFloatingDialogInstance,
              }}
            >
              <FloatingPanelContextProvider
                value={{
                  containerRef: floatingPanelContainerRef,
                  setOffset: handleSetFloatingOffset,
                }}
              >
                <GuidanceOverlayContextProvider
                  value={{ container: guidanceOverlayContainer }}
                >
                  <div
                    css={{
                      color: uiColors.text,
                      height: "100%",
                    }}
                  >
                    <SnackbarContainer>
                      <OverlayContextProvider
                        value={{
                          container: childrenContainerRef,
                        }}
                      >
                        <div
                          ref={childrenContainerRef}
                          {...(isAnyDialogOpen && {
                            inert: inertValue,
                            "aria-hidden": "true",
                          })}
                          css={{
                            isolation: "isolate",
                            height: "100%",
                            boxSizing: "border-box",
                            // Workaround for https://github.com/adobe/react-spectrum/issues/1513
                            // Сorrects the triggering of clicks in child content
                            // when a dialog box is open
                            pointerEvents:
                              isAnyDialogOpen || debouncedDialogOpen
                                ? "none"
                                : undefined,
                          }}
                        >
                          {children}
                        </div>
                        <div
                          ref={floatingContainerRef}
                          {...(isRegularDialogOpen && {
                            inert: inertValue,
                            "aria-hidden": "true",
                          })}
                          css={{
                            position: "fixed",
                            display: "flex",
                            flexDirection: "column",
                            boxSizing: "border-box",
                            rowGap: "8px",
                            height: isFloatingDialogOpen
                              ? `calc(100vh - ${floatingOffsetBlock + 16}px)`
                              : undefined,
                            insetInlineEnd: `${floatingOffsetInline + 16}px`,
                            insetBlockEnd: `${floatingOffsetBlock + 16}px`,
                          }}
                        >
                          <div
                            css={{
                              display: "flex",
                              flexGrow: "1",
                              minHeight: "0",
                              ...(isMobile && isFloatingDialogOpen
                                ? {
                                    position: "fixed",
                                    ...cssFns.inset("0"),
                                    zIndex: "1",
                                  }
                                : undefined),
                            }}
                            ref={floatingDialogContainerRef}
                          />
                          <div
                            css={{
                              display: "flex",
                              justifyContent: "flex-end",
                              columnGap: "4px",
                            }}
                            ref={floatingPanelContainerRef}
                          />
                        </div>
                        <div ref={(element) => setPopoverContainer(element)} />
                        <div
                          css={{
                            insetBlockEnd: "0",
                            insetInlineEnd: "0",
                            insetInlineStart: "0",
                            position: "fixed",
                          }}
                          ref={appMenuContainerRef}
                        />
                        <div ref={slideoverContainerRef} />
                        <div ref={dialogContainerRef} />
                      </OverlayContextProvider>
                    </SnackbarContainer>
                  </div>
                  <div
                    ref={(element) => setGuidanceOverlayContainer(element)}
                  />
                </GuidanceOverlayContextProvider>
              </FloatingPanelContextProvider>
            </FloatingDialogContextProvider>
          </PopoverContextProvider>
        </SlideoverContextProvider>
      </DialogContextProvider>
    </AppMenuContextProvider>
  );
};
